import { Fragment, Node as ProseMirrorNode, Schema } from '@tiptap/pm/model';
import { SELECTION_MARK_NAME, SELECTION_NODE_NAME } from '../constants';

/**
 * Recursively remove all `selection-node` wrappers and strip all `selection-mark` marks.
 * This code is used for removing all selections from the content pasted from the clipboard.
 *
 */
const unwrapSelectionFragment = (fragment: Fragment, schema: Schema): Fragment => {
  const unwrappedNodes: ProseMirrorNode[] = [];
  // console.log('fragment', fragment);

  fragment.forEach((child) => {
    // filter out selection-mark from this node’s marks
    const filteredMarks = child.marks.filter((mark) => mark.type.name !== SELECTION_MARK_NAME);

    if (child.type.name === SELECTION_NODE_NAME) {
      // "unwrap" the selection-node: flatten its children into the parent
      const inner = unwrapSelectionFragment(child.content, schema);
      inner.forEach((grandchild) => {
        unwrappedNodes.push(grandchild);
      });
    } else {
      // if it's a TEXT NODE, reconstruct via schema.text(...).
      if (child.isText) {
        // recreate a text node with the filtered marks
        const textNode = schema.text(child.text || '', filteredMarks);
        unwrappedNodes.push(textNode);
      }
      // ff it's a LEAF NODE (non-text), create the same node with no children
      else if (child.isLeaf) {
        const newLeaf = child.type.create(
          child.attrs,
          null, // no child content
          filteredMarks
        );
        unwrappedNodes.push(newLeaf);
      }
      // otherwise, recursively handle this node’s children
      else {
        const newContent = unwrapSelectionFragment(child.content, schema);
        const newNode = child.type.create(child.attrs, newContent, filteredMarks);
        unwrappedNodes.push(newNode);
      }
    }
  });

  return Fragment.fromArray(unwrappedNodes);
};

export default unwrapSelectionFragment;
